/**
 * AppRouter Component
 * Main application router configuration using React Router v6.
 */

import { Suspense } from 'react';
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';
import { MainLayout } from '@layouts/MainLayout';
import { AuthLayout } from '@layouts/AuthLayout';
import { ProtectedRoute } from './ProtectedRoute';
// import { PublicRoute } from './PublicRoute';
import { ROUTES } from '@config/constants';

// Lazy-loaded page components
import { lazy } from 'react';

// Registration Module Pages
const LoginPage = lazy(() => import('@modules/registration/pages/LoginPage'));
const RegisterPage = lazy(() => import('@modules/registration/pages/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('@modules/registration/pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('@modules/registration/pages/ResetPasswordPage'));
const RegistrationManagementPage = lazy(() => import('@modules/registration/pages/RegistrationManagementPage'));
const LinkRegistrationsPage = lazy(() => import('@modules/registration/pages/LinkRegistrationsPage'));
const ContractTemplatesPage = lazy(() => import('@modules/registration/pages/ContractTemplatesPage'));
const ProductConfigurationsPage = lazy(() =>
  import('@modules/product-config').then((m) => ({ default: m.ProductConfigurationsPage }))
);
const OnboardingPage = lazy(() => import('@modules/registration/pages/OnboardingPage'));
const PublicRegistrationPage = lazy(() => import('@modules/registration/pages/PublicRegistrationPage'));
const ProfilePage = lazy(() => import('@modules/registration/pages/ProfilePage'));

// Dashboard Daie Module Pages
const DashboardDaiePage = lazy(() => import('@modules/dashboard-daie/pages/DashboardDaiePage'));
const IncentiveDashboardPage = lazy(() => import('@modules/dashboard-daie/pages/IncentiveDashboardPage'));
const AllIncentivesPage = lazy(() => import('@modules/dashboard-daie/pages/AllIncentivesPage'));

// Sales Module Pages
const SalesPage = lazy(() => import('@modules/sales/pages/SalesPage'));
const QuotationPage = lazy(() => import('@modules/sales/quotation/pages/QuotationPage'));

// Training Module Pages
const TrainingPage = lazy(() => import('@modules/training/pages/TrainingPage'));
const AdminTrainingPage = lazy(() => import('@modules/training/pages/AdminTrainingPage'));
const CourseEnrollmentPage = lazy(() => import('@modules/training/pages/CourseEnrollmentPage'));
const TrainingOverviewPage = lazy(() => import('@modules/training/pages/TrainingOverviewPage'));
const CourseDetailPage = lazy(() => import('@modules/training/pages/CourseDetailPage'));
const CourseContentPage = lazy(() => import('@modules/training/pages/CourseContentPage'));
const PhysicalProgramsPage = lazy(() => import('@modules/training/pages/PhysicalProgramsPage'));
const CreatePhysicalProgramPage = lazy(() => import('@modules/training/pages/CreatePhysicalProgramPage'));
const PhysicalProgramDetailPage = lazy(() => import('@modules/training/pages/PhysicalProgramDetailPage'));
const EditPhysicalProgramPage = lazy(() => import('@modules/training/pages/EditPhysicalProgramPage'));
const EnterResultsPage = lazy(() => import('@modules/training/pages/EnterResultsPage'));
const ScanAttendancePage = lazy(() => import('@modules/training/pages/ScanAttendancePage'));
const MyPhysicalProgramsPage = lazy(() => import('@modules/training/pages/MyPhysicalProgramsPage'));

// Dashboard Report Module Pages
const DashboardReportPage = lazy(() => import('@modules/dashboard-report/pages/DashboardReportPage'));
const AdminDashboardPage = lazy(() => import('@modules/dashboard-report/pages/AdminDashboardPage'));
const ReportsPage = lazy(() => import('@modules/dashboard-report/pages/ReportsPage'));
const SingleReportPage = lazy(() => import('@modules/dashboard-report/pages/SingleReportPage'));
const ManagerOverviewPage = lazy(() => import('@modules/dashboard-report/pages/Manager/OverviewPage'));
const AgentMonitoringPage = lazy(() => import('@modules/dashboard-report/pages/AgentMonitoringPage').then(module => ({ default: module.AgentMonitoringPage })));
const KPICompliancePage = lazy(() => import('@modules/dashboard-report/pages/KPICompliancePage').then(module => ({ default: module.KPICompliancePage })));
const KPIConfigurationPage = lazy(() => import('@modules/dashboard-report/pages/KPIConfigurationPage').then(module => ({ default: module.KPIConfigurationPage })));
const ExecutiveDashboardPage = lazy(() => import('@modules/dashboard-report/pages/Management/ExecutiveDashboardPage'));
const HQReportDownloadPage = lazy(() => import('@modules/dashboard-report/pages/Management/HQReportDownloadPage'));
const DaieManagementPage = lazy(() => import('@modules/dashboard-report/pages/Admin/DaieManagementPage'));
const RolesManagementPage = lazy(() =>
  import('@modules/roles-management').then((m) => ({ default: m.RolesManagementPage }))
);

// Memo Module Pages
const MemoPage = lazy(() => import('@modules/memo/pages/MemoPage'));
const NotificationsPage = lazy(() => import('@modules/memo/daie/pages/NotificationsPage/index'));

// Chatbot Module Pages
const ChatbotPage = lazy(() => import('@modules/chatbot/pages/ChatbotPage'));

// Support Inquiries Module Pages (Admin)
const SupportInquiriesPage = lazy(() => import('@modules/support-inquiries/pages/SupportInquiriesPage'));
const InquiryDetailPage = lazy(() => import('@modules/support-inquiries/pages/InquiryDetailPage'));

/**
 * Loading fallback component for Suspense.
 */
const PageLoader = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
    {/* TODO: Replace with proper loading component */}
    <p>Loading...</p>
  </div>
);

/**
 * Router configuration.
 * NOTE: Route guards disabled for development. Re-enable for production.
 */
const router = createBrowserRouter([
  // Public routes with AuthLayout
  {
    element: <AuthLayout />,
    children: [
      {
        path: ROUTES.LOGIN,
        element: (
          <Suspense fallback={<PageLoader />}>
            <LoginPage />
          </Suspense>
        ),
      },
      {
        path: ROUTES.REGISTER,
        element: (
          <Suspense fallback={<PageLoader />}>
            <RegisterPage />
          </Suspense>
        ),
      },
      {
        path: ROUTES.FORGOT_PASSWORD,
        element: (
          <Suspense fallback={<PageLoader />}>
            <ForgotPasswordPage />
          </Suspense>
        ),
      },
      {
        path: ROUTES.RESET_PASSWORD,
        element: (
          <Suspense fallback={<PageLoader />}>
            <ResetPasswordPage />
          </Suspense>
        ),
      },
      {
        path: ROUTES.ONBOARDING,
        element: (
          <Suspense fallback={<PageLoader />}>
            <OnboardingPage />
          </Suspense>
        ),
      },
      {
        path: `${ROUTES.REGISTER}/form`,
        element: (
          <Suspense fallback={<PageLoader />}>
            <PublicRegistrationPage />
          </Suspense>
        ),
      },
      {
        path: `${ROUTES.TRAINING_PHYSICAL_PROGRAMS}/:programId/scan`,
        element: (
          <Suspense fallback={<PageLoader />}>
            <ScanAttendancePage />
          </Suspense>
        ),
      },
    ],
  },

  // Protected routes with MainLayout (guards disabled for dev)
  {
    element: <MainLayout />,
    children: [
      {
        path: ROUTES.ADMIN_DASHBOARD,
        element: (
          <Suspense fallback={<PageLoader />}>
            <AdminDashboardPage />
          </Suspense>
        ),
      },
      {
        path: ROUTES.DASHBOARD,
        element: (
          <Suspense fallback={<PageLoader />}>
            <DashboardDaiePage />
          </Suspense>
        ),
      },
      {
        path: ROUTES.DASHBOARD_DAIE,
        element: (
          <Suspense fallback={<PageLoader />}>
            <DashboardDaiePage />
          </Suspense>
        ),
      },
      {
        path: ROUTES.SALES,
        element: (
          <Suspense fallback={<PageLoader />}>
            <SalesPage />
          </Suspense>
        ),
      },
      {
        path: ROUTES.SALES_QUOTATIONS,
        element: (
          <Suspense fallback={<PageLoader />}>
            <QuotationPage />
          </Suspense>
        ),
      },
      {
        path: `${ROUTES.SALES}/*`,
        element: (
          <Suspense fallback={<PageLoader />}>
            <SalesPage />
          </Suspense>
        ),
      },
      {
        path: ROUTES.SALES_QUOTATIONS,
        element: (
          <Suspense fallback={<PageLoader />}>
            <QuotationPage />
          </Suspense>
        ),
      },
      {
        path: ROUTES.TRAINING,
        element: (
          <Suspense fallback={<PageLoader />}>
            <TrainingOverviewPage />
          </Suspense>
        ),
      },
      {
        path: ROUTES.TRAINING_MY_PROGRAMS,
        element: (
          <Suspense fallback={<PageLoader />}>
            <MyPhysicalProgramsPage />
          </Suspense>
        ),
      },
      {
        path: `${ROUTES.TRAINING}/course/:courseId`,
        element: (
          <Suspense fallback={<PageLoader />}>
            <CourseDetailPage />
          </Suspense>
        ),
      },
      {
        path: `${ROUTES.TRAINING}/course/:courseId/content/:chapterId?`,
        element: (
          <Suspense fallback={<PageLoader />}>
            <CourseContentPage />
          </Suspense>
        ),
      },
      {
        path: `${ROUTES.TRAINING}/admin`,
        element: (
          <Suspense fallback={<PageLoader />}>
            <AdminTrainingPage />
          </Suspense>
        ),
      },
      {
        path: `${ROUTES.TRAINING}/courses/:courseId/enrollments`,
        element: (
          <Suspense fallback={<PageLoader />}>
            <CourseEnrollmentPage />
          </Suspense>
        ),
      },
      {
        path: ROUTES.TRAINING_PHYSICAL_PROGRAMS,
        element: (
          <Suspense fallback={<PageLoader />}>
            <PhysicalProgramsPage />
          </Suspense>
        ),
      },
      {
        path: `${ROUTES.TRAINING_PHYSICAL_PROGRAMS}/create`,
        element: (
          <Suspense fallback={<PageLoader />}>
            <CreatePhysicalProgramPage />
          </Suspense>
        ),
      },
      {
        path: `${ROUTES.TRAINING_PHYSICAL_PROGRAMS}/:programId/edit`,
        element: (
          <Suspense fallback={<PageLoader />}>
            <EditPhysicalProgramPage />
          </Suspense>
        ),
      },
      {
        path: `${ROUTES.TRAINING_PHYSICAL_PROGRAMS}/:programId`,
        element: (
          <Suspense fallback={<PageLoader />}>
            <PhysicalProgramDetailPage />
          </Suspense>
        ),
      },
      {
        path: `${ROUTES.TRAINING_PHYSICAL_PROGRAMS}/:programId/results`,
        element: (
          <Suspense fallback={<PageLoader />}>
            <EnterResultsPage />
          </Suspense>
        ),
      },
      {
        path: ROUTES.DASHBOARD_REPORT,
        element: <Navigate to={ROUTES.DASHBOARD_REPORT_SALES} replace />,
      },
      {
        path: ROUTES.DASHBOARD_REPORT_SALES,
        element: (
          <Suspense fallback={<PageLoader />}>
            <SingleReportPage />
          </Suspense>
        ),
      },
      {
        path: ROUTES.DASHBOARD_REPORT_CONTRACT,
        element: (
          <Suspense fallback={<PageLoader />}>
            <SingleReportPage />
          </Suspense>
        ),
      },
      {
        path: ROUTES.DASHBOARD_REPORT_TRAINING,
        element: (
          <Suspense fallback={<PageLoader />}>
            <SingleReportPage />
          </Suspense>
        ),
      },
      {
        path: ROUTES.DASHBOARD_REPORT_EXPORT,
        element: (
          <Suspense fallback={<PageLoader />}>
            <SingleReportPage />
          </Suspense>
        ),
      },
      {
        path: ROUTES.SYSTEM_CONFIG,
        element: <Navigate to={ROUTES.ROLES_MANAGEMENT} replace />,
      },
      {
        path: ROUTES.MANAGER_OVERVIEW,
        element: (
          <Suspense fallback={<PageLoader />}>
            <ManagerOverviewPage />
          </Suspense>
        ),
      },
      {
        path: ROUTES.AGENT_MONITORING,
        element: (
          <Suspense fallback={<PageLoader />}>
            <AgentMonitoringPage />
          </Suspense>
        ),
      },
      {
        path: ROUTES.KPI_COMPLIANCE,
        element: (
          <Suspense fallback={<PageLoader />}>
            <KPICompliancePage />
          </Suspense>
        ),
      },
      {
        path: ROUTES.KPI_CONFIGURATION,
        element: (
          <Suspense fallback={<PageLoader />}>
            <KPIConfigurationPage />
          </Suspense>
        ),
      },
      {
        path: ROUTES.EXECUTIVE_DASHBOARD,
        element: (
          <ProtectedRoute requiredRoles={['executive', 'admin']}>
            <Suspense fallback={<PageLoader />}>
              <ExecutiveDashboardPage />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.HQ_REPORT_DOWNLOAD,
        element: (
          <ProtectedRoute requiredRoles={['executive', 'admin']}>
            <Suspense fallback={<PageLoader />}>
              <HQReportDownloadPage />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.DAIE_MANAGEMENT,
        element: (
          <Suspense fallback={<PageLoader />}>
            <DaieManagementPage />
          </Suspense>
        ),
      },
      {
        path: ROUTES.ROLES_MANAGEMENT,
        element: (
          <ProtectedRoute requiredRoles={['admin']}>
            <Suspense fallback={<PageLoader />}>
              <RolesManagementPage />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.MEMO,
        element: (
          <Suspense fallback={<PageLoader />}>
            <MemoPage />
          </Suspense>
        ),
      },
      {
        path: ROUTES.NOTIFICATIONS,
        element: (
          <Suspense fallback={<PageLoader />}>
            <NotificationsPage />
          </Suspense>
        ),
      },
      {
        path: ROUTES.CHATBOT,
        element: (
          <Suspense fallback={<PageLoader />}>
            <ChatbotPage />
          </Suspense>
        ),
      },
      {
        path: ROUTES.REGISTRATION_MANAGEMENT,
        element: (
          <ProtectedRoute requiredRoles={['admin']}>
            <Suspense fallback={<PageLoader />}>
              <RegistrationManagementPage />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.LINK_REGISTRATIONS,
        element: (
          <ProtectedRoute requiredRoles={['admin']}>
            <Suspense fallback={<PageLoader />}>
              <LinkRegistrationsPage />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.PROFILE,
        element: (
          <Suspense fallback={<PageLoader />}>
            <ProfilePage />
          </Suspense>
        ),
      },
      {
        path: ROUTES.INCENTIVES,
        element: (
          <ProtectedRoute requiredRoles={['admin']}>
            <Suspense fallback={<PageLoader />}>
              <IncentiveDashboardPage />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.INCENTIVE_HISTORY,
        element: (
          <ProtectedRoute requiredRoles={['daie']}>
            <Suspense fallback={<PageLoader />}>
              <AllIncentivesPage />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.CONTRACT_TEMPLATES,
        element: (
          <ProtectedRoute requiredRoles={['admin']}>
            <Suspense fallback={<PageLoader />}>
              <ContractTemplatesPage />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.PRODUCT_CONFIG,
        element: (
          <ProtectedRoute requiredRoles={['admin']}>
            <Suspense fallback={<PageLoader />}>
              <ProductConfigurationsPage />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.SUPPORT_INQUIRIES,
        element: (
          <ProtectedRoute requiredRoles={['admin']}>
            <Suspense fallback={<PageLoader />}>
              <SupportInquiriesPage />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: `${ROUTES.SUPPORT_INQUIRIES}/:id`,
        element: (
          <ProtectedRoute requiredRoles={['admin']}>
            <Suspense fallback={<PageLoader />}>
              <InquiryDetailPage />
            </Suspense>
          </ProtectedRoute>
        ),
      },
    ],
  },

  // Root redirect (send first-time visitors to login)
  {
    path: ROUTES.HOME,
    element: <Navigate to={ROUTES.LOGIN} replace />,
  },

  // 404 fallback
  {
    path: '*',
    element: <Navigate to={ROUTES.LOGIN} replace />,
  },
]);

/**
 * Application router component.
 * Wraps the entire application with routing context.
 */
export const AppRouter = () => {
  return <RouterProvider router={router} />;
};

